'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface MapStory {
  id: number;
  location: string;
  country: string;
  lat: number;
  lng: number;
  count: number;
  category: string;
}

interface GlobalStoryMapProps {
  stories: MapStory[];
}

const GlobalStoryMap: React.FC<GlobalStoryMapProps> = ({ stories }) => {
  const [selectedLocation, setSelectedLocation] = useState<MapStory | null>(null);
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    return (
      <div className="bg-card rounded-lg p-8 shadow-md">
        <div className="h-[500px] bg-muted rounded-lg animate-pulse" />
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg p-8 shadow-md">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-2xl font-headline font-bold text-foreground mb-2">
            Global Transformation Map
          </h3>
          <p className="text-muted-foreground font-body">
            Stories of healing and reconciliation from around the world
          </p>
        </div>
        <div className="flex items-center space-x-2 px-4 py-2 bg-accent/10 rounded-lg">
          <Icon name="GlobeAltIcon" size={24} className="text-accent" />
          <div>
            <div className="text-2xl font-headline font-bold text-foreground">
              {stories.reduce((sum, story) => sum + story.count, 0)}
            </div>
            <div className="text-xs text-muted-foreground font-body">Total Stories</div>
          </div>
        </div>
      </div>

      <div className="relative h-[500px] bg-muted rounded-lg overflow-hidden">
        <iframe
          width="100%"
          height="100%"
          loading="lazy"
          title="Global Story Map"
          referrerPolicy="no-referrer-when-downgrade"
          src="https://www.google.com/maps?q=-6.369028,34.888822&z=2&output=embed"
          className="rounded-lg"
        />

        <div className="absolute top-4 right-4 bg-card rounded-lg shadow-lg p-4 max-w-xs">
          <div className="flex items-center space-x-2 mb-3">
            <Icon name="MapPinIcon" size={20} className="text-primary" />
            <span className="font-cta text-sm font-bold text-foreground">Story Locations</span>
          </div>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {stories.map((story) => (
              <button
                key={story.id}
                onClick={() => setSelectedLocation(story)}
                className={`w-full text-left p-2 rounded-md transition-colors duration-200 ${
                  selectedLocation?.id === story.id
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-muted'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-body text-sm font-medium">{story.location}</div>
                    <div className="text-xs opacity-80">{story.country}</div>
                  </div>
                  <div className="flex items-center space-x-1">
                    <span className="text-xs font-bold">{story.count}</span>
                    <Icon name="DocumentTextIcon" size={14} />
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {selectedLocation && (
        <div className="mt-6 p-6 bg-muted rounded-lg">
          <div className="flex items-start justify-between">
            <div>
              <h4 className="text-xl font-headline font-bold text-foreground mb-2">
                {selectedLocation.location}, {selectedLocation.country}
              </h4>
              <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                <div className="flex items-center space-x-1">
                  <Icon name="DocumentTextIcon" size={16} />
                  <span>{selectedLocation.count} stories</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name="TagIcon" size={16} />
                  <span>{selectedLocation.category}</span>
                </div>
              </div>
            </div>
            <button
              onClick={() => setSelectedLocation(null)}
              className="p-2 hover:bg-background rounded-md transition-colors duration-200"
              aria-label="Close location details"
            >
              <Icon name="XMarkIcon" size={20} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalStoryMap;